import React, { useEffect } from 'react';
import { apps } from '../data/apps';
import { users } from '../data/users';

interface DashboardProps {
  userEmail: string;
  onLogout: () => void;
  onAppAccess: (appId: string, variantId?: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ userEmail, onLogout, onAppAccess }) => {
  const currentUser = users.find(u => u.email === userEmail);
  const userApps = apps.filter(app => app.allowedUsers.includes(userEmail));

  useEffect(() => {
    // Check if user returned from OAuth flow
    const targetApp = sessionStorage.getItem('sso_target_app');
    if (targetApp) {
      // Clear the stored app and show success message
      sessionStorage.removeItem('sso_target_app');
      sessionStorage.removeItem('sso_return_url');
      sessionStorage.removeItem('sso_user_email');
      
      // Find the app and redirect user to it
      const app = apps.find(a => a.id === targetApp);
      if (app) {
        // Show success message briefly then redirect
        const successMsg = document.createElement('div');
        successMsg.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #28a745;
          color: white;
          padding: 1rem;
          border-radius: 4px;
          z-index: 1000;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        `;
        successMsg.textContent = `Successfully logged into ${app.name}! Redirecting...`;
        document.body.appendChild(successMsg);
        
        setTimeout(() => {
          document.body.removeChild(successMsg);
          // Redirect to the app's main URL (they should now be logged in)
          window.open(app.url, '_blank');
        }, 2000);
      }
    }
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '"Segoe UI", "Roboto", "Arial", sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #e9ecef',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#0056b3',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.25rem',
            border: '2px solid #004494'
          }}>
            M
          </div>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '1.75rem', 
              fontWeight: '700',
              color: '#1a202c',
              letterSpacing: '0'
            }}>
              MACT AI
            </h1>
            <p style={{ 
              margin: 0, 
              fontSize: '0.875rem', 
              color: '#6c757d',
              fontWeight: '400'
            }}>
              Insurance Technology Platform
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              backgroundColor: '#6c757d',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}>
              {currentUser?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ 
                color: '#1a202c', 
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                {currentUser?.name}
              </div>
              <div style={{ 
                color: '#6c757d', 
                fontSize: '0.75rem'
              }}>
                {userEmail}
              </div>
            </div>
          </div>
          <button
            onClick={onLogout}
            style={{
              padding: '0.625rem 1.5rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#c82333';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#dc3545';
            }}
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        padding: '2rem',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        <div style={{ 
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid #dee2e6'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            color: '#1a202c',
            margin: '0 0 0.5rem 0'
          }}>
            Application Portal
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#6c757d',
            margin: 0
          }}>
            Access your insurance technology applications and tools
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '1.5rem'
        }}>
          {userApps.map(app => (
            <div
              key={app.id}
              style={{
                backgroundColor: '#ffffff',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #dee2e6',
                transition: 'box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  margin: '0 0 0.75rem 0',
                  color: '#1a202c',
                  fontSize: '1.25rem',
                  fontWeight: '600'
                }}>
                  {app.name}
                </h3>
                
                <p style={{
                  color: '#6c757d',
                  margin: 0,
                  lineHeight: '1.5',
                  fontSize: '0.95rem'
                }}>
                  {app.description}
                </p>
              </div>
              
              {/* Render variants or single button */}
              {app.variants && app.variants.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {app.variants.map(variant => {
                    // Check if user has access to this variant
                    const userCreds = app.credentials?.[userEmail] as any;
                    const hasAccess = userCreds && (typeof userCreds[variant.id] !== 'undefined' || !app.requiresLogin);
                    
                    if (!hasAccess) return null;
                    
                    // Enterprise color scheme
                    let backgroundColor: string;
                    let borderColor: string;
                    let textColor: string;
                    if (variant.id === 'production') {
                      backgroundColor = '#28a745';
                      borderColor = '#1e7e34';
                      textColor = 'white';
                    } else if (variant.id === 'staging') {
                      backgroundColor = '#ffc107';
                      borderColor = '#e0a800';
                      textColor = '#212529';
                    } else {
                      backgroundColor = '#007bff';
                      borderColor = '#0056b3';
                      textColor = 'white';
                    }
                    
                    return (
                      <button
                        key={variant.id}
                        onClick={() => onAppAccess(app.id, variant.id)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          backgroundColor: backgroundColor,
                          color: textColor,
                          border: `1px solid ${borderColor}`,
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '0.9rem',
                            marginBottom: '0.2rem'
                          }}>
                            {variant.name} Environment
                          </div>
                          <div style={{ 
                            opacity: variant.id === 'staging' ? 0.8 : 0.9, 
                            fontSize: '0.8rem',
                            fontWeight: '400'
                          }}>
                            {variant.description}
                          </div>
                        </div>
                        <div style={{ 
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          →
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <button
                  onClick={() => onAppAccess(app.id)}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1.25rem',
                    backgroundColor: '#0056b3',
                    color: 'white',
                    border: '1px solid #004494',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#004494';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#0056b3';
                  }}
                >
                  {app.requiresLogin ? 'Access Application' : 'Open Application'}
                </button>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;